import { ErrorSeverity, RemiApp, RemiAppError } from "@remi/error";

/**
 * Writes text to the clipboard using the legacy execCommand fallback.
 * Mobile Safari/Chrome and unfocused documents can reject the modern
 * Clipboard API — the execCommand path is more lenient.
 */
function writeToClipboardFallback(text: string): void {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new RemiAppError({
      app: RemiApp.REMI_NEXTJS,
      errorType: "CLIPBOARD_COPY_FAILED",
      severity: ErrorSeverity.LOW,
      data: {},
    });
  }
}

/**
 * Copies text to the clipboard with a fallback for environments where
 * `navigator.clipboard.writeText` is unavailable or throws (e.g. the
 * document is not focused, or the Permissions Policy blocks clipboard
 * access).
 */
export async function copyTextToClipboard(text: string): Promise<void> {
  try {
    // oxlint-disable-next-line no-restricted-properties -- utility module that owns the raw clipboard call
    await navigator.clipboard.writeText(text);
  } catch {
    writeToClipboardFallback(text);
  }
}

export async function copyTextToClipboardAsync(textPromise: Promise<string>): Promise<void> {
  const blob = textPromise.then((text) => new Blob([text], { type: "text/plain" }));
  // oxlint-disable-next-line no-restricted-properties -- utility module that owns the raw clipboard call
  await navigator.clipboard.write([new ClipboardItem({ "text/plain": blob })]);
}

export async function copyHtmlToClipboard(html: string, text: string): Promise<void> {
  const item = new ClipboardItem({
    "text/html": new Blob([html], { type: "text/html" }),
    "text/plain": new Blob([text], { type: "text/plain" }),
  });
  try {
    // oxlint-disable-next-line no-restricted-properties -- utility module that owns the raw clipboard call
    await navigator.clipboard.write([item]);
  } catch {
    writeToClipboardFallback(text);
  }
}
